<template>
    <div class="bg-white" :class="containerClasses">
        <live-edit-input
            class="shrink-0 bg-white"
            :class="inputClasses"
            input-extra-class-name="border-opacity-100"
            type="numeric"
            mask="decimal"
            :disabled="disabled"
            :allow-negative-value="allowNegativeValue"
            :animated="false"
            :model-value="value"
            @update:model-value="handleValueChange"
        >
            <template #before-input>
                <div :class="currencySymbolClasses" v-if="!isFrLocale">
                    <span class="text-black" data-testid="money-prefix" v-text="'$'" />
                </div>
            </template>

            <template #after-input>
                <div :class="currencySymbolClasses" v-if="isFrLocale">
                    <span class="text-black" data-testid="money-suffix" v-text="'$'" />
                </div>
            </template>
        </live-edit-input>
    </div>
</template>

<script setup lang="ts">
    /**
     * For when we don't need all possible options, just a generic money input
     * with a prefix and a suffix.
     */
    import { computed } from 'vue';
    import { useI18n } from '@/hooks/useI18n.ts';
    import LiveEditInput from '@/components/container/input/LiveEditInput.vue';

    const props = withDefaults(
        defineProps<{
            value?: number;
            disabled?: boolean;
            useDefaultWidth?: boolean;
            inputClassList?: string;
            hideCurrencySymbolOnMobile?: boolean;
            allowNegativeValue?: boolean;
            containerClassList?: string;
        }>(),
        {
            value: 0,
            disabled: false,
            useDefaultWidth: true,
            hideCurrencySymbolOnMobile: false,
            allowNegativeValue: false,
            containerClassList: '',
        }
    );

    const emit = defineEmits<{
        (e: 'input', value: number): void;
    }>();

    const { isFrLocale } = useI18n();

    const containerClasses = computed(() => props.containerClassList);

    const inputClasses = computed(() => {
        if (props.inputClassList) {
            return props.inputClassList;
        }

        return props.useDefaultWidth ? 'w-38 xs:w-52 md:w-38 xl:w-48' : '';
    });

    const currencySymbolClasses = computed(() => {
        const baseClasses = 'flex justify-center items-center w-10 bg-gray-300';
        if (props.hideCurrencySymbolOnMobile) {
            return `${baseClasses} hidden md:flex`;
        }
        return baseClasses;
    });

    const handleValueChange = (value: number) => {
        emit('input', value);
    };
</script>
